// AVRASTRO AI Content Studio — the CLAIMS LEDGER and the deterministic guardrails (requirement 09, binding;
// docs/content-studio/FEASIBILITY.md §6). Pure and browser-safe: the platform server runs these checks before an asset can
// go to review or be approved, and the app runs the very same checks while the advisor edits (instant feedback).
//
//  1. Claims ledger: approved PRODUCT FACTS of the workspace, each with its source document, page, validity date and the
//     approver; optionally attached to a catalogue item (insurer → category → plan, requirement 14). A creative may state a
//     product-specific fact only when it cites (attaches) a ledger entry.
//  2. Deterministic checks on every text of a creative:
//       - numbers, percentages and currency amounts must match a value of an attached, valid ledger entry;
//       - a block-list of risk phrases in Greek, English and Russian (it contains every pattern of the weekly content
//         engine's quality gate, tools/content-engine/lib.mjs BANNED — a test keeps the two in step);
//       - statements that a product COVERS / PAYS medical care only from an attached medical ledger entry;
//       - the mandatory footer «General information, not advice.» (tools/video/posts.mjs NOTE) and, where the AVRASTRO
//         name or mark appears, «AVRASTRO is not an insurer.»;
//       - marketing must be identifiable as such (a visible «Marketing communication» label) and AI-generated media must
//         carry the AI label (EU AI Act art. 50).
//  3. The AI reviewer (platform, second pass) is ADVISORY only; a person's approval is always required (studio-assets.ts).
// What this cannot do (said honestly in the UI): understand numbers written as words, or judge tone. That is what the
// advisor's approval is for.

export type StudioLang = 'el' | 'en' | 'ru';
export const studioLang = (v: unknown): StudioLang => (v === 'en' ? 'en' : v === 'ru' ? 'ru' : 'el');

/** the insurance topics of the template library and of ledger entries (requirement 09) */
export const STUDIO_TOPICS = ['life', 'medical', 'child_savings', 'education', 'retirement', 'personal_accident', 'income_protection', 'loan_protection', 'generic'] as const;
export type StudioTopic = typeof STUDIO_TOPICS[number];

export interface ClaimSource { docId: string | null; name: string; page: string }
export interface ClaimEntry {
  id: string;
  topic: StudioTopic;
  /** the product as the advisor names it (free text, e.g. the plan's name) */
  productLabel: string;
  /** the approved fact, worded as in the source */
  statement: string;
  lang: StudioLang;
  /** the numeric values the statement contains (derived; what a creative may quote) */
  values: NumericToken[];
  catalog: { insurerId?: string | null; categoryId?: string | null; planId?: string | null } | null;
  source: ClaimSource;
  validFrom: string | null;
  /** last day the fact may be used (YYYY-MM-DD) */
  validUntil: string;
  approvedBy: string;
  approvedAt: string;
  status: 'active' | 'retired';
  createdAt: string;
  updatedAt: string;
}

// --- text folding ------------------------------------------------------------------------------------------------------
/** lower case, accents and diaeresis removed, final sigma folded (for matching only) */
export function foldStudio(s: string): string {
  return String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/ς/g, 'σ').replace(/ё/g, 'е').replace(/й/g, 'и');
}
const L = '\\p{L}\\p{N}';
/** a Unicode word-edge pattern: JavaScript's \b only knows Latin letters */
const re = (src: string, flags = 'u') => new RegExp(src.replaceAll('\\b', `(?:(?<![${L}])(?=[${L}])|(?<=[${L}])(?![${L}]))`), flags);

// --- numbers -------------------------------------------------------------------------------------------------------------
export type NumUnit = 'pct' | 'eur' | 'num';
export interface NumericToken { raw: string; unit: NumUnit; /** every value the text can mean (1.500 = 1500 in Greek, 1.5 in English) */ values: number[] }

const CUR_BEFORE = /(?:€|eur\s?|euro\s?)$/i;
const CUR_AFTER = /^\s?(?:€|eur\b|euro|ευρω|ευρώ|евро|евр)/iu;
const PCT_AFTER = /^\s?(?:%|τοισ εκατο|τοις εκατό|percent|per cent|процент\p{L}*)/iu;
/** the possible values of a written number: separators read both ways when ambiguous */
function readings(raw: string): number[] {
  const s = raw.replace(/\s/g, '');
  const out = new Set<number>();
  const plain = (x: string) => { const n = Number(x); if (Number.isFinite(n)) out.add(n); };
  if (!/[.,]/.test(s)) { plain(s); return [...out]; }
  const lastSep = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','));
  const tail = s.slice(lastSep + 1);
  // decimal reading (the last separator is the decimal mark, the others group thousands)
  plain(s.slice(0, lastSep).replace(/[.,]/g, '') + '.' + tail);
  // thousands reading (every separator groups thousands) when the groups are of three digits
  if (/^\d{1,3}([.,]\d{3})+$/.test(s)) plain(s.replace(/[.,]/g, ''));
  return [...out];
}
/** numeric tokens in a text: amounts, percentages and plain numbers; template placeholders {{…}} are skipped */
export function numericTokens(text: string, opts: { skipYears?: boolean } = {}): NumericToken[] {
  const t = String(text ?? '').replace(/\{\{[^}]*\}\}/g, ' ');
  const out: NumericToken[] = [];
  const NUM = /(?<![\p{L}\p{N}])\d{1,3}(?:[ .,]\d{3})+(?:[.,]\d+)?(?![\p{N}])|(?<![\p{L}\p{N}])\d+(?:[.,]\d+)?(?![\p{N}])/gu;
  for (const m of t.matchAll(NUM)) {
    const raw = m[0].trim();
    const before = t.slice(Math.max(0, m.index! - 5), m.index!);
    const after = t.slice(m.index! + m[0].length, m.index! + m[0].length + 14);
    const unit: NumUnit = PCT_AFTER.test(foldStudio(after)) || PCT_AFTER.test(after) ? 'pct' : CUR_BEFORE.test(before) || CUR_AFTER.test(after) || CUR_AFTER.test(foldStudio(after)) ? 'eur' : 'num';
    const values = readings(raw);
    // a calendar year on its own (e.g. «2026») is not a product fact
    if (opts.skipYears !== false && unit === 'num' && /^(19|20)\d{2}$/.test(raw)) continue;
    out.push({ raw, unit, values });
  }
  return out;
}
const digitsOnly = (s: string) => String(s ?? '').replace(/\D/g, '');
/** does a token of the copy match one of the ledger's values (same unit; a bare number may stand for an amount or percent) */
export function tokenMatches(tok: NumericToken, allowed: readonly NumericToken[]): boolean {
  return allowed.some((a) => (tok.unit === 'num' || a.unit === tok.unit) && a.values.some((v) => tok.values.some((w) => Math.abs(v - w) < 1e-9)));
}

// --- risk phrases --------------------------------------------------------------------------------------------------------
/**
 * Phrases that promise what no advisor may promise (IDD art. 17: fair, clear, not misleading). Matched on folded text
 * (no accents, lower case). Each entry: [language or 'any', pattern source]. Includes every pattern of the content engine's
 * gate (tools/content-engine/lib.mjs BANNED; tests/unit/studio-claims.test.ts keeps them in step).
 */
export const RISK_PHRASES: readonly { lang: StudioLang | 'any'; src: string; code: string }[] = [
  // the content engine's gate (owner pipelines, tools/content-engine/lib.mjs BANNED), folded; the website-only rules that
  // say AVRASTRO never sells («get a quote», «buy from us», «click here») are left out: an advisor does sell, by approval
  { lang: 'any', src: 'guaranteed (return|profit|income)', code: 'guarantee' },
  { lang: 'any', src: 'risk[- ]free', code: 'risk_free' },
  { lang: 'any', src: 'best (insurance|policy|plan) for you', code: 'best' },
  { lang: 'any', src: '\\bthe cheapest\\b', code: 'cheapest' },
  { lang: 'any', src: '\\bbuy now\\b', code: 'pressure' },
  { lang: 'any', src: 'limited[- ]time offer', code: 'pressure' },
  { lang: 'any', src: 'act now', code: 'pressure' },
  { lang: 'any', src: "\\b(our|avrastro['’]s) (insurance )?(products|policies|plans) (cover|offer|include|start|cost)", code: 'own_products' },
  { lang: 'any', src: 'avrastro (is|as) (an|your) (insurer|insurance company|broker)', code: 'avrastro_insurer' },
  { lang: 'any', src: 'you (should|must) (buy|cancel|switch)', code: 'advice' },
  { lang: 'any', src: 'we (advise|recommend) (you )?to (buy|cancel|switch|take out)', code: 'advice' },
  { lang: 'any', src: '\\bstop taking\\b', code: 'medical_advice' },
  { lang: 'any', src: '\\bdiagnos(e|is) (you|your)', code: 'medical_advice' },
  { lang: 'any', src: '\\bthis is (legal|medical|tax) advice\\b', code: 'advice' },
  { lang: 'el', src: 'εγγυημενη αποδοση', code: 'guarantee' },
  { lang: 'el', src: 'χωρισ (κανενα )?ρισκο', code: 'risk_free' },
  { lang: 'el', src: 'η καλυτερη ασφαλεια για εσασ', code: 'best' },
  { lang: 'el', src: 'αγοραστε τωρα', code: 'pressure' },
  { lang: 'el', src: 'μοναδικη ευκαιρια', code: 'pressure' },
  { lang: 'el', src: '(τα|τισ) (συμβολαια|σχεδια|προιοντα|καλυψεισ) μασ (καλυπτ|προσφερ|περιλαμβ|κοστιζ)', code: 'own_products' },
  { lang: 'el', src: '(πρεπει|οφειλετε) να (αγορασετε|ακυρωσετε)', code: 'advice' },
  { lang: 'el', src: 'σασ (συνιστουμε|συμβουλευουμε) να (αγορασετε|ακυρωσετε|αλλαξετε)', code: 'advice' },
  { lang: 'el', src: 'σταματηστε (τη θεραπεια|το φαρμακο)', code: 'medical_advice' },
  { lang: 'ru', src: 'гарантированн\\S* доход', code: 'guarantee' },
  { lang: 'ru', src: 'без (всякого )?риска', code: 'risk_free' },
  { lang: 'ru', src: 'купите сеичас', code: 'pressure' },
  { lang: 'ru', src: 'наш\\S* (полис|страховк|продукт)\\S* (покрыва|предлага|включа|стои)', code: 'own_products' },
  { lang: 'ru', src: 'вы должны (купить|расторгнуть)', code: 'advice' },
  { lang: 'ru', src: 'мы (советуем|рекомендуем) (вам )?(купить|расторгнуть|сменить)', code: 'advice' },
  { lang: 'ru', src: 'прекратите (лечение|прием)', code: 'medical_advice' },
  // the studio's own (requirement 09: never invented benefits, guaranteed returns, exclusions or medical cover)
  { lang: 'any', src: 'guaranteed (returns?|profits?|income|payouts?|growth|gains?)', code: 'guarantee' },
  { lang: 'any', src: '\\b(covers|cover|covering) (everything|it all|all risks|anything)\\b', code: 'covers_everything' },
  { lang: 'any', src: '\\b(no|without( any)?|zero) exclusions?\\b', code: 'no_exclusions' },
  { lang: 'any', src: '\\b(the )?best (policy|policies|insurance|cover|plan|plans)\\b', code: 'best' },
  { lang: 'any', src: '\\b(the )?(cheapest|lowest[- ]priced) (policy|insurance|cover|plan)\\b', code: 'cheapest' },
  { lang: 'any', src: '\\b100 ?% (covered|cover|safe|guaranteed|protection|protected)\\b', code: 'covers_everything' },
  { lang: 'any', src: '\\b(never|no) (worry|worries) again\\b', code: 'promise' },
  { lang: 'any', src: '\\bclaims? (are )?(always|guaranteed to be) paid\\b', code: 'promise' },
  { lang: 'el', src: 'εγγυημεν\\p{L}* (αποδοσ\\p{L}*|κερδ\\p{L}*|εισοδημ\\p{L}*|αποζημιωσ\\p{L}*)', code: 'guarantee' },
  { lang: 'el', src: 'καλυπτ\\p{L}* (τα παντα|τα ολα|ολα|οτιδηποτε|καθε κινδυνο)', code: 'covers_everything' },
  { lang: 'el', src: 'πληρησ καλυψη για (ολα|τα παντα)', code: 'covers_everything' },
  { lang: 'el', src: '(χωρισ|καμια|μηδεν) εξαιρεσ\\p{L}*', code: 'no_exclusions' },
  { lang: 'el', src: '(το |η |τα |οι )?καλυτερ\\p{L}* (συμβολαι\\p{L}*|ασφαλει\\p{L}*|ασφαλισ\\p{L}*|σχεδι\\p{L}*|καλυψ\\p{L}*)', code: 'best' },
  { lang: 'el', src: '(το |η )?φθηνοτερ\\p{L}* (συμβολαι\\p{L}*|ασφαλει\\p{L}*|ασφαλισ\\p{L}*|σχεδι\\p{L}*)', code: 'cheapest' },
  { lang: 'el', src: '100 ?% (καλυψη|καλυμμεν\\p{L}*|εγγυημεν\\p{L}*|ασφαλ\\p{L}*)', code: 'covers_everything' },
  { lang: 'el', src: 'χωρισ (καμια )?(ανησυχια|εγνοια) ποτε', code: 'promise' },
  { lang: 'ru', src: 'гарантированн\\S* (доходност\\S*|прибыл\\S*|выплат\\S*)', code: 'guarantee' },
  { lang: 'ru', src: 'покрыва\\S* (все|всё|любые риски|что угодно)', code: 'covers_everything' },
  { lang: 'ru', src: 'без (каких-либо |всяких )?исключени\\S*', code: 'no_exclusions' },
  { lang: 'ru', src: 'лучш\\S* (полис\\S*|страховк\\S*|страховани\\S*|план\\S*)', code: 'best' },
  { lang: 'ru', src: 'сам\\S* дешев\\S* (полис\\S*|страховк\\S*)', code: 'cheapest' },
];
const RISK_RE = RISK_PHRASES.map((r) => ({ ...r, re: re(r.src, 'iu') }));
/** the risk phrases found in a text (every language is checked: a Greek post may quote an English slogan) */
export function riskPhrases(text: string): { code: string; quote: string }[] {
  const f = foldStudio(text);
  const out: { code: string; quote: string }[] = [];
  for (const r of RISK_RE) { const m = r.re.exec(f); if (m && !out.some((o) => o.quote === m[0])) out.push({ code: r.code, quote: m[0] }); }
  return out;
}

// --- medical coverage statements -----------------------------------------------------------------------------------------
const COVER_VERB = re(('\\b(covers|covered|cover[s]? the|includes?|included|pays?|pays? for|pays? out|provides?|offers?|reimburses?|refunds?|no waiting period|καλυπτ(ει|ουν|εται|ονται)|καλυφθ\\p{L}*|περιλαμβαν(ει|ουν|εται|ονται)|πληρων(ει|ουν|εται|ονται)|αποζημιων(ει|ουν|εται|ονται)|επιστρεφ(ει|ουν|εται|ονται)|προσφερ(ει|ουν|εται|ονται)|παρεχ(ει|ουν|εται|ονται)|χωρισ περιοδο αναμονησ|покрыва(ет|ют|ется|ются)|включа(ет|ют|ется|ются)|оплачива(ет|ют|ется|ются)|возмеща(ет|ют|ется|ются)|выплачива(ет|ют|ется|ются)|предоставля(ет|ют|ется|ются))\\b'), 'iu');
const MEDICAL = re(('\\b(hospital\\p{L}*|surger\\p{L}*|operation\\p{L}*|medical|medicine\\p{L}*|treatment\\p{L}*|cancer|doctor\\p{L}*|specialist\\p{L}*|diagnos\\p{L}*|maternity|dental|chronic|pre-existing|outpatient|inpatient|ambulance|gesy|νοσοκομ\\p{L}*|νοσηλ\\p{L}*|κλινικ\\p{L}*|εντατικ\\p{L}*|χειρουργ\\p{L}*|ιατρ\\p{L}*|θεραπει\\p{L}*|καρκιν\\p{L}*|φαρμακ\\p{L}*|γιατρ\\p{L}*|διαγνωσ\\p{L}*|τοκετ\\p{L}*|μητροτητ\\p{L}*|οδοντ\\p{L}*|χρονι\\p{L}* (νοσ|παθησ)\\p{L}*|προυπαρχ\\p{L}*|ασθενοφορ\\p{L}*|γεσυ|γεσυ|больниц\\p{L}*|стационар\\p{L}*|операци\\p{L}*|медицин\\p{L}*|лечени\\p{L}*|рак\\p{L}*|врач\\p{L}*|лекарств\\p{L}*|роды|стоматолог\\p{L}*|хроническ\\p{L}*)\\b'), 'iu');
const PASSIVE = re('\\b(covered|included|paid|reimbursed|καλυπτ(εται|ονται)|καλυφθ\\p{L}*|περιλαμβαν(εται|ονται)|πληρων(εται|ονται)|αποζημιων(εται|ονται)|покрыва(ется|ются)|включа(ется|ются)|оплачива(ется|ются)|возмеща(ется|ются))\\b', 'u');
/** a sentence that states that something COVERS / PAYS medical care (generic mentions of «medical cover» are fine) */
export function isMedicalCoverageStatement(sentence: string): boolean {
  const f = foldStudio(sentence);
  const g = new RegExp(COVER_VERB.source, 'gu');
  for (const m of f.matchAll(g)) {
    const after = f.slice(m.index! + m[0].length, m.index! + m[0].length + 70);
    if (MEDICAL.test(after)) return true; // «covers surgery», «καλύπτει νοσηλεία»
    // the passive puts the care first: «surgery is covered», «η νοσηλεία καλύπτεται»
    if (PASSIVE.test(m[0]) && MEDICAL.test(f.slice(Math.max(0, m.index! - 70), m.index!))) return true;
  }
  return false;
}
const OWN_PRODUCT = re('\\b(this|our|my) (plan|policy|product|programme|program|cover)\\b|\\b(αυτο το|αυτη η) (σχεδιο|συμβολαιο|προγραμμα|προιον|ασφαλιστηριο|καλυψη)\\b|\\b(σχεδιο|συμβολαιο|προγραμμα|προιον|ασφαλιστηριο) μασ\\b|\\b(этот|эта|наш|наша) (план|полис|продукт|программа|страховка)\\b', 'iu');
/**
 * a sentence that states what a SPECIFIC product covers, includes or pays («this plan pays …», «το σχέδιό μας καλύπτει …», or
 * a product named in the ledger / catalogue) — needs a ledger entry. Generic education («life cover can help a family …»)
 * is not a product claim.
 */
export function isCoverageStatement(sentence: string, productNames: readonly string[] = []): boolean {
  const f = foldStudio(sentence);
  if (!COVER_VERB.test(f)) return false;
  return OWN_PRODUCT.test(f) || productNames.some((n) => n.trim().length >= 3 && f.includes(foldStudio(n.trim())));
}
const SECOND_PERSON = re('\\b(your|you|σασ|σου|ваш\\p{L}*|вы|вас)\\b', 'u');
const STEM_STOP = new Set(['και', 'για', 'του', 'της', 'των', 'τον', 'την', 'στο', 'στη', 'στην', 'απο', 'οτι', 'ενα', 'μια', 'the', 'and', 'for', 'with', 'from', 'that', 'this', 'your', 'you', 'are', 'its', 'это', 'для', 'что', 'при', 'как']);
const stems = (s: string): Set<string> => new Set(foldStudio(s).split(/[^\p{L}\p{N}]+/u).filter((w) => w.length >= 3 && !STEM_STOP.has(w)).map((w) => w.slice(0, 5)));
/** share of a sentence's content words that come from the statement (0…1) */
export function overlap(sentence: string, statement: string): number {
  const a = stems(sentence), b = stems(statement);
  if (!a.size) return 0;
  let n = 0; for (const w of a) if (b.has(w)) n++;
  return n / a.size;
}
/** sentences of a text (for the per-sentence rules) */
export const sentencesOf = (text: string): string[] => String(text ?? '').split(/(?<=[.!?;·;])\s+|\n+/u).map((s) => s.trim()).filter(Boolean);

// --- mandatory wording ----------------------------------------------------------------------------------------------------
/** «General information, not advice.» — the note of the owner's social posts (tools/video/posts.mjs NOTE), word for word */
export const STUDIO_NOTE: Record<StudioLang, string> = { el: 'Γενική ενημέρωση, όχι συμβουλή.', en: 'General information, not advice.', ru: 'Общая информация, а не рекомендация.' };
/** where the AVRASTRO name or mark appears (the content engine's NOT_ADVICE wording) */
export const NOT_INSURER: Record<StudioLang, string> = { el: 'Το AVRASTRO δεν είναι ασφαλιστική εταιρεία.', en: 'AVRASTRO is not an insurer.', ru: 'AVRASTRO не является страховой компанией.' };
/** IDD art. 17(2): marketing communications shall always be clearly identifiable as such */
export const MARKETING_LABEL: Record<StudioLang, string> = { el: 'Διαφημιστική ενημέρωση', en: 'Marketing communication', ru: 'Рекламная информация' };
/** EU AI Act art. 50: synthetic images and video are labelled (and marked machine-readably in the export) */
export const AI_LABEL: Record<StudioLang, string> = { el: 'Εικόνα δημιουργημένη με AI', en: 'AI-generated image', ru: 'Изображение создано ИИ' };
export const AI_VIDEO_LABEL: Record<StudioLang, string> = { el: 'Περιέχει πλάνα δημιουργημένα με AI', en: 'Contains AI-generated footage', ru: 'Содержит кадры, созданные ИИ' };
/** the footer every creative of the studio carries (the template engine draws it; nobody can remove it) */
export function mandatoryFooter(lang: StudioLang, opts: { avrastro: boolean }): string {
  return [STUDIO_NOTE[lang], opts.avrastro ? NOT_INSURER[lang] : ''].filter(Boolean).join(' ');
}
const mentionsAvrastro = (s: string) => /avrastro/i.test(s);

// --- the checks -----------------------------------------------------------------------------------------------------------
export type IssueSeverity = 'block' | 'warn';
export type IssueCode = 'number_without_claim' | 'risk_phrase' | 'medical_without_claim' | 'coverage_without_claim' | 'claim_expired' | 'claim_unknown' | 'footer_missing'
  | 'not_insurer_missing' | 'marketing_label_missing' | 'ai_label_missing' | 'personal_data' | 'insurer_name' | 'text_too_long';
export interface CheckIssue { code: IssueCode; severity: IssueSeverity; field: string; quote?: string; detail?: string }
export interface CheckResult { ok: boolean; issues: CheckIssue[]; checkedAt: string; version: 1 }

export interface CheckInput {
  lang: StudioLang;
  /** every text the creative shows or publishes (headline, body, CTA, caption, script, hashtags …) */
  texts: Record<string, string>;
  /** the footer drawn on the creative */
  footer: string;
  labels: { marketing: boolean; ai: boolean };
  /** the AVRASTRO mark appears in the layout (co-branding) */
  avrastroMark: boolean;
  /** any AI-generated image or video in the creative */
  aiMedia: boolean;
  /** ledger entries the creative cites (ids) and the workspace ledger to resolve them */
  claimRefs: readonly string[];
  ledger: readonly ClaimEntry[];
  today: string;
  /** numbers that are the advisor's own contact or licence details (brand kit), never product facts */
  allowedNumbers?: readonly string[];
  /** insurer names of the catalogue: a warning while insurer branding is off (owner decision D15) */
  insurerNames?: readonly string[];
  /** product names (catalogue plans, ledger products): a coverage statement naming one needs a ledger entry */
  productNames?: readonly string[];
}

/** run every deterministic rule; `ok` is false when any issue blocks (review and approval then refuse the asset) */
export function checkCreative(i: CheckInput): CheckResult {
  const issues: CheckIssue[] = [];
  const cited = i.claimRefs.map((id) => i.ledger.find((c) => c.id === id) ?? null);
  for (const [k, c] of i.claimRefs.map((id, n) => [id, cited[n]] as const)) {
    if (!c) issues.push({ code: 'claim_unknown', severity: 'block', field: 'claims', detail: k });
    else if (c.status !== 'active' || c.validUntil < i.today || (c.validFrom && c.validFrom > i.today)) issues.push({ code: 'claim_expired', severity: 'block', field: 'claims', detail: c.id, quote: c.statement.slice(0, 80) });
  }
  const valid = cited.filter((c): c is ClaimEntry => !!c && c.status === 'active' && c.validUntil >= i.today && (!c.validFrom || c.validFrom <= i.today));
  const allowed = valid.flatMap((c) => c.values);
  // the advisor's own phone / licence numbers (brand kit): compared on their last 8 digits (a Cypriot number without +357)
  const own = new Set((i.allowedNumbers ?? []).map(digitsOnly).filter((d) => d.length >= 4).map((d) => d.slice(-8)));
  for (const [field, text] of Object.entries(i.texts)) {
    if (!text) continue;
    // personal contact details of anyone but the advisor never go into a public creative
    for (const m of String(text).matchAll(/[^\s@]+@[^\s@]+\.[a-z]{2,}/gi)) issues.push({ code: 'personal_data', severity: 'warn', field, quote: m[0] });
    // phone numbers first (+357 99 123456, 22-123456, 99123456): the advisor's own are fine, any other is not a product fact
    let rest = String(text);
    for (const m of String(text).matchAll(/(?<![\d.,])(?:(?:\+|00)\d{3}[ .-]?)?\d{2}[ .-]?\d{6}(?![\d.,]\d)|(?<![\d.,])\d{8,}(?![\d.,]\d)/g)) {
      const d = digitsOnly(m[0]);
      if (d.length < 8) continue;
      rest = rest.replace(m[0], ' ');
      if (!own.has(d.slice(-8))) issues.push({ code: 'number_without_claim', severity: 'block', field, quote: m[0].trim() });
    }
    for (const tok of numericTokens(rest)) {
      if (own.has(digitsOnly(tok.raw).slice(-8))) continue;
      if (tokenMatches(tok, allowed)) continue;
      issues.push({ code: 'number_without_claim', severity: 'block', field, quote: tok.raw });
    }
    for (const r of riskPhrases(text)) issues.push({ code: 'risk_phrase', severity: 'block', field, quote: r.quote, detail: r.code });
    for (const s of sentencesOf(text)) {
      // a question about the reader's OWN cover («Do you know what your medical cover provides?») states nothing about a product
      if (/[?;\u037e]\s*$/u.test(s) && SECOND_PERSON.test(foldStudio(s))) continue;
      if (isMedicalCoverageStatement(s)) {
        const med = valid.filter((c) => c.topic === 'medical');
        if (!med.some((c) => overlap(s, c.statement) >= 0.6)) issues.push({ code: 'medical_without_claim', severity: 'block', field, quote: s.slice(0, 120) });
      } else if (isCoverageStatement(s, [...(i.productNames ?? []), ...i.ledger.map((c) => c.productLabel)])) {
        if (!valid.some((c) => overlap(s, c.statement) >= 0.5)) issues.push({ code: 'coverage_without_claim', severity: 'block', field, quote: s.slice(0, 120) });
      }
    }
    for (const n of i.insurerNames ?? []) if (n.trim().length >= 3 && foldStudio(text).includes(foldStudio(n.trim()))) issues.push({ code: 'insurer_name', severity: 'warn', field, quote: n });
  }
  const f = foldStudio(i.footer);
  if (!f.includes(foldStudio(STUDIO_NOTE[i.lang]))) issues.push({ code: 'footer_missing', severity: 'block', field: 'footer', detail: STUDIO_NOTE[i.lang] });
  const avr = i.avrastroMark || Object.values(i.texts).some(mentionsAvrastro);
  if (avr && !f.includes(foldStudio(NOT_INSURER[i.lang]))) issues.push({ code: 'not_insurer_missing', severity: 'block', field: 'footer', detail: NOT_INSURER[i.lang] });
  if (!i.labels.marketing) issues.push({ code: 'marketing_label_missing', severity: 'block', field: 'labels' });
  if (i.aiMedia && !i.labels.ai) issues.push({ code: 'ai_label_missing', severity: 'block', field: 'labels' });
  return { ok: !issues.some((x) => x.severity === 'block'), issues, checkedAt: i.today, version: 1 };
}

// --- ledger entries --------------------------------------------------------------------------------------------------------
export interface LedgerInput {
  topic: string; productLabel: string; statement: string; lang?: string;
  catalog?: { insurerId?: string | null; categoryId?: string | null; planId?: string | null } | null;
  source: { docId?: string | null; name: string; page: string };
  validFrom?: string | null; validUntil: string;
}
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
/** validate a new ledger entry: a source document, its page, a validity date and a statement are all required */
export function claimProblems(c: LedgerInput, today: string): string[] {
  const p: string[] = [];
  if (!(STUDIO_TOPICS as readonly string[]).includes(c.topic)) p.push('topic');
  if (!String(c.productLabel ?? '').trim()) p.push('product');
  const st = String(c.statement ?? '').trim();
  if (st.length < 8 || st.length > 600) p.push('statement');
  if (!String(c.source?.name ?? '').trim()) p.push('source');
  if (!String(c.source?.page ?? '').trim() || String(c.source?.page).length > 40) p.push('page');
  if (!DAY_RE.test(String(c.validUntil ?? '')) || String(c.validUntil) < today) p.push('valid_until');
  if (c.validFrom && (!DAY_RE.test(c.validFrom) || c.validFrom > c.validUntil)) p.push('valid_from');
  // the ledger holds product facts, never a person's details
  if (/[^\s@]+@[^\s@]+\.[a-z]{2,}/i.test(st) || /\b(?:2|9)\d[ .-]?\d{6}\b/.test(st)) p.push('personal_data');
  return p;
}
/** the facts a creative may quote, for the copy writer (ids the model must not invent) */
export function ledgerForPrompt(entries: readonly ClaimEntry[], today: string): { ref: string; topic: StudioTopic; product: string; fact: string }[] {
  return entries.filter((c) => c.status === 'active' && c.validUntil >= today).map((c, n) => ({ ref: `F${n + 1}`, topic: c.topic, product: c.productLabel, fact: c.statement }));
}
